import { NestingRunView } from "../api/nestingContract";
import { Layout, MaterialLayout, QuoteRequestLine } from "../types";
import { adaptComputedCost } from "./adaptComputedCost";

type Params = {
  nestingRun: Pick<NestingRunView, "items" | "sheetsUsed"> | null;
  layout: Pick<Layout, "newScrapMaterialBillingRate"> & {
    materialLayout: Pick<MaterialLayout, "length" | "width" | "price"> | null;
  };
  lines: Pick<QuoteRequestLine, "id" | "quantity">[];
};

export const computeNestingUnitMaterialCosts = ({ nestingRun, layout, lines }: Params) => {
  const unitCosts = new Map<string, number>();
  const sheet = layout.materialLayout;
  const sheetAreaMm2 = sheet ? sheet.length * sheet.width : 0;
  const sheetPrice = sheet?.price ?? null;
  const sheetsUsed = nestingRun?.sheetsUsed ?? 0;

  if (sheetPrice === null || sheetAreaMm2 === 0 || sheetsUsed === 0 || !nestingRun) {
    return unitCosts;
  }

  const quantityByLineId = new Map(lines.map(({ id, quantity }) => [id, quantity]));
  const totalNetAreaMm2 = nestingRun.items.reduce(
    (total, { itemId, netAreaMm2 }) => total + netAreaMm2 * (quantityByLineId.get(itemId) ?? 0),
    0,
  );

  if (totalNetAreaMm2 === 0) {
    return unitCosts;
  }

  const totalSheetsAreaMm2 = sheetsUsed * sheetAreaMm2;
  const scrapAreaMm2 = Math.max(totalSheetsAreaMm2 - totalNetAreaMm2, 0);
  const billedScrapShare = layout.newScrapMaterialBillingRate / 100;
  const billingFactor = 1 + (billedScrapShare * scrapAreaMm2) / totalNetAreaMm2;
  const pricePerMm2 = sheetPrice / sheetAreaMm2;

  nestingRun.items.forEach(({ itemId, netAreaMm2 }) => {
    const unitCost = netAreaMm2 * pricePerMm2 * billingFactor;
    unitCosts.set(itemId, adaptComputedCost({ cost: unitCost, marginPrice: 0 }).cost);
  });

  return unitCosts;
};
